import logger from '@/client/utils/logger'
import type {
	AlgoliaCollection,
	AlgoliaPlaylist,
	QueryHit,
} from '@/utils/algolia/autocompleteHitTypes'
import { normalizeExecuteError } from '@/utils/algolia/debounceAlgoliaQuery'

import { buildAutocompleteQueries, getAutocompleteIndices } from './queries'
import { mapAutocompleteResults } from './results'
import {
	type AutocompleteResult,
	createEmptyAutocompleteResult,
	type FetchAutocompleteParams,
} from './types'
import {
	normalizeAutocompleteLanguageCode,
	shouldFetchAutocompleteForQuery,
} from './utils'

type AutocompleteSearchHit = QueryHit | AlgoliaPlaylist | AlgoliaCollection

export const fetchAutocomplete = async ({
	searchClient,
	query,
	refinementList,
	languageCode,
	user,
}: FetchAutocompleteParams): Promise<AutocompleteResult> => {
	if (!shouldFetchAutocompleteForQuery(query)) {
		return createEmptyAutocompleteResult()
	}

	try {
		const { requestDescriptors, searchableTitleAttributes } =
			buildAutocompleteQueries({
				query,
				refinementList,
				languageCode: normalizeAutocompleteLanguageCode(languageCode),
				indices: getAutocompleteIndices(),
			})

		const { results } = await searchClient.search<AutocompleteSearchHit>(
			requestDescriptors.map(({ request }) => request)
		)

		const { autocompleteResult, sectionMappingErrors } = mapAutocompleteResults(
			results,
			requestDescriptors,
			searchableTitleAttributes,
			user
		)

		sectionMappingErrors.forEach((sectionError) => {
			logger('Autocomplete: section mapping failed', sectionError.error, {
				query,
				section: sectionError.section,
			})
		})

		return autocompleteResult
	} catch (error) {
		const normalizedError = normalizeExecuteError(error)
		logger('Autocomplete: request or configuration failed', normalizedError, {
			query,
		})

		return createEmptyAutocompleteResult()
	}
}
